class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (!cartItems) return;
      cartItems.updateQuantity(this.dataset.key || this.dataset.index, 0, undefined, this.dataset.index, {
        variantId: this.dataset.variantId,
        role: this.dataset.lineRole
      });
    });
  }
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    if (!event.target.matches('.quantity__input')) return;

    const line = event.target.dataset.lineKey || event.target.dataset.index;
    const visualLine = event.target.dataset.index;
    if (!line) return;

    this.updateQuantity(
      line,
      event.target.value,
      document.activeElement && document.activeElement.getAttribute('name'),
      visualLine,
      {
        variantId: event.target.dataset.quantityVariantId,
        role: event.target.dataset.lineRole
      }
    );
  }

  onCartUpdate() {
    fetch('/cart?section_id=main-cart-items')
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch(e => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  updateQuantity(line, quantity, name, visualLine, options = {}) {
    if (!line) return;

    const lineForDom = visualLine || line;
    this.enableLoading(lineForDom);
    const isDrawerUpdate = this.closest('cart-drawer') || this.tagName.toLowerCase() === 'cart-drawer-items';

    if (
      isDrawerUpdate &&
      window.CushyBumCartOffer &&
      typeof window.CushyBumCartOffer.changeLineAndRender === 'function'
    ) {
      window.CushyBumCartOffer.changeLineAndRender({
        key: String(line || ''),
        visualLine: Number(lineForDom || 0),
        variantId: String(options.variantId || ''),
        role: String(options.role || '')
      }, quantity, { openDrawer: true })
        .then(() => {
          publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cushy-bum-cart-offer'});
        })
        .catch((error) => {
          const message = error && error.message ? error.message : window.cartStrings.error;
          this.updateLiveRegions(lineForDom, message);
        })
        .finally(() => {
          this.disableLoading(lineForDom);
        });
      return;
    }

    const changePayload = {
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    };

    if (String(line).indexOf(':') !== -1) {
      changePayload.id = line;
    } else {
      changePayload.line = line;
    }

    const body = JSON.stringify(changePayload);

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        let parsedState;

        try {
          parsedState = JSON.parse(state);
        } catch (error) {
          this.refreshCartDrawer();
          return;
        }

        const quantityElement = document.getElementById(`Quantity-${lineForDom}`) || document.getElementById(`Drawer-quantity-${lineForDom}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors || parsedState.status) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');

          const errorText = parsedState.description || parsedState.errors || '';
          const normalizedError = String(errorText).toLowerCase();
          const removingItem = Number(quantity) === 0;
          const staleLineError =
            removingItem ||
            normalizedError.indexOf('line') !== -1 ||
            normalizedError.indexOf('cannot find variant') !== -1;

          if (!staleLineError) {
            this.updateLiveRegions(lineForDom, window.cartStrings.error);
          }

          this.refreshCartDrawer();
          return;
        }

        if (!parsedState.sections) {
          this.refreshCartDrawer();
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        if (isDrawerUpdate && cartDrawerWrapper && typeof cartDrawerWrapper.renderContents === 'function') {
          const source = 'cart-items';

          cartDrawerWrapper.renderContents(parsedState);

          if (
            window.CushyBumCartOffer &&
            typeof window.CushyBumCartOffer.syncAndRender === 'function'
          ) {
            window.CushyBumCartOffer.syncAndRender({
              openDrawer: true,
              fallbackState: parsedState
            }).then(() => {
              publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cushy-bum-cart-offer'});
            }).catch(() => {
              publish(PUB_SUB_EVENTS.cartUpdate, {source});
            });
          } else {
            publish(PUB_SUB_EVENTS.cartUpdate, {source});
          }

          return;
        }

        this.getSectionsToRender().forEach((section => {
          const sectionContainer = document.getElementById(section.id);
          const sectionMarkup = parsedState.sections[section.section];
          if (!sectionContainer || !sectionMarkup) return;

          const elementToReplace =
            sectionContainer.querySelector(section.selector) || sectionContainer;
          const replacementHTML = this.getSectionInnerHTML(sectionMarkup, section.selector);
          if (!replacementHTML) return;

          elementToReplace.innerHTML = 
            replacementHTML;
        }));
        const updatedValue = parsedState.items[lineForDom - 1] ? parsedState.items[lineForDom - 1].quantity : undefined;
        let message = '';
        if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(lineForDom, message);

        const lineItem = document.getElementById(`CartItem-${lineForDom}`) || document.getElementById(`CartDrawer-Item-${lineForDom}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        this.refreshCartDrawer();
      })
      .finally(() => {
        this.disableLoading(lineForDom);
      });
  }

  refreshCartDrawer() {
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    if (!cartDrawerWrapper || typeof cartDrawerWrapper.getSectionsToRender !== 'function') return;

    fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => response.json())
      .then((cart) => {
        return fetch(`${window.location.pathname}?sections=${cartDrawerWrapper.getSectionsToRender().map((section) => section.id).join(',')}`, {
          headers: {
            Accept: 'application/json'
          }
        })
          .then((response) => response.json())
          .then((sections) => {
            cart.sections = sections;
            return cart;
          });
      })
      .then((cart) => {
        cartDrawerWrapper.renderContents(cart);
      })
      .catch((error) => {
        console.error('Cart refresh failed', error);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    const lineItemErrorText = lineItemError && lineItemError.querySelector('.cart-item__error-text');
    if (lineItemErrorText) lineItemErrorText.innerHTML = message;

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return parsed ? parsed.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    const isDrawerUpdate = this.closest('cart-drawer') || this.tagName.toLowerCase() === 'cart-drawer-items';
    if (mainCartItems && !isDrawerUpdate) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    if (document.activeElement && !isDrawerUpdate) document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    const isDrawerUpdate = this.closest('cart-drawer') || this.tagName.toLowerCase() === 'cart-drawer-items';
    if (mainCartItems && !isDrawerUpdate) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
      constructor() {
        super();

      this.addEventListener('change', debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, ON_CHANGE_DEBOUNCE_TIMER))
      }
  });
};

function handleDiscountForm(e) {
  e.preventDefault();

  const discountInput = e.target.querySelector('[name=cart-discount-field]');
  const discountError = e.target.querySelector('.cart-discount-form__error');
  const discountValue = discountInput.value;
  if (discountValue === undefined || discountValue.length === 0) {
    discountError.style.display = 'block';
    return;
  }
  discountError.style.display = 'none';
  const checkoutBaseUrl = '/checkout?discount=';
  const newCheckoutUrl = checkoutBaseUrl + discountValue;
  window.location.href = newCheckoutUrl;
}

function handleDiscountFormChange(e) {
  const discountErros = document.querySelectorAll('.cart-discount-form__error');
  discountErros.forEach(error => {
    error.style.display = 'none'
  })
}
